import { useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { AccountActionDialog, type AccountTarget } from "./AccountActionDialog";
import { createSubmitLock } from "./action-model";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { copyDevAccount } from "@/lib/api";

export function CopyDevAccountDialog({ target, saveVersion, onClose }: {
  target: AccountTarget; saveVersion: number; onClose: () => void;
}) {
  const submitting = useRef(createSubmitLock());
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => copyDevAccount(target.id, saveVersion),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["dev-accounts"] });
      void qc.invalidateQueries({ queryKey: ["audit"] });
    },
    onSettled: () => { submitting.current.end(); },
  });
  return <AccountActionDialog title="Copy as dev account" target={target}
    scope="Creates a separate developer account from the saved game data." pending={mutation.isPending}
    changed={false} onClose={onClose}>
    <p className="text-sm">Copy the saved progress, purchases and auxiliary save data into a new dev account.
      It will use a generated <code>dev_randomnumber@gmail.com</code> email and password <code>password</code>.</p>
    <Button disabled={mutation.isPending || mutation.isSuccess} onClick={() => {
      if (submitting.current.begin()) mutation.mutate();
    }}>{mutation.isPending ? "Copying…" : "Create dev copy"}</Button>
    {mutation.error && <Alert variant="destructive"><AlertDescription>
      {mutation.error.message === "save_conflict" ? "The source save changed. Close this dialog, refresh and try again." : String(mutation.error)}
    </AlertDescription></Alert>}
    {mutation.data && <div role="status" className="space-y-2 rounded-md border border-border p-3 text-sm">
      <p>Dev account created.</p>
      <p>Email: <code className="select-all">{mutation.data.email}</code></p>
      <p>Password: <code className="select-all">{mutation.data.password}</code></p>
      <p>UID: <code className="select-all">{mutation.data.playerId}</code></p>
      <Link className="text-primary underline" to={`/players/${mutation.data.playerId}`}>Open new dev account</Link>
    </div>}
  </AccountActionDialog>;
}
